import type { ReactNode } from 'react';
import { Box, CircularProgress, Stack, Typography, styled, useTheme } from '@mui/material';
import { IconBrain, IconClockHour4, IconMicrophone, IconTypography } from '@tabler/icons-react';
import { CustomTooltip } from '@shared/components/customTooltip';
import {
  useCompanyOverviewControllerGetCompanyOverviewQuery,
  useUsageLimitControllerUsageLimitsQuery,
  type UsageLimitDetailDto,
  type UsageUnitLimit,
} from '@shared/services/apiService/apiService';
import { USAGE_UNITS } from '@widgets/Popup/CompanyLimitsPopup/constants';
import { formatUsedLimitPairAsHours } from '@widgets/Popup/CompanyLimitsPopup/utils';
import { formatCompactUsagePair } from '@shared/utils/formatCompactCount';
import { t } from 'i18next';
import {
  IconFileStop,
  IconFilePlay,
  IconFileStack,
  IconPhoneCall,
} from '@shared/assets/images/svgs';

const BoxStyled = styled(Box)(() => ({
  padding: '16px 16px',
  transition: '0.1s ease-in',
  cursor: 'pointer',
  color: 'inherit',
  '&:hover': {
    transform: 'scale(1.03)',
  },
}));

const IconBox = styled(Box)(() => ({
  width: '40px',
  height: '40px',
  minWidth: '40px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
}));

function findLimitRow(
  limits: UsageLimitDetailDto[],
  unit: UsageUnitLimit,
): UsageLimitDetailDto | undefined {
  return limits.find((r) => r.unit === unit);
}

function MetricValueTooltip({ title, children }: { title: string; children: ReactNode }) {
  if (!title || title === '—') {
    return children;
  }
  return (
    <CustomTooltip
      title={title}
      placement="top"
      arrow
      slotProps={{
        tooltip: {
          sx: {
            maxWidth: 'min(90vw, 520px)',
            wordBreak: 'break-word',
          },
        },
      }}>
      <span>{children}</span>
    </CustomTooltip>
  );
}

const gridContainerSx = {
  display: 'grid',
  gap: 2,
  gridTemplateColumns: {
    xs: 'repeat(2, minmax(0, 1fr))',
    sm: 'repeat(3, minmax(0, 1fr))',
    md: 'repeat(4, minmax(0, 1fr))',
    lg: 'repeat(5, minmax(0, 1fr))',
  },
} as const;

export const FilterCard = () => {
  const theme = useTheme();
  const { data: companyOverview, isLoading: overviewLoading } =
    useCompanyOverviewControllerGetCompanyOverviewQuery();
  const { data: usageData, isLoading: usageLoading } = useUsageLimitControllerUsageLimitsQuery();

  const limits = usageData?.limits ?? [];
  const callRow = findLimitRow(limits, USAGE_UNITS.callSecond);
  const llmRow = findLimitRow(limits, USAGE_UNITS.llmTotalTokens);
  const sttRow = findLimitRow(limits, USAGE_UNITS.sttSecond);
  const ttsRow = findLimitRow(limits, USAGE_UNITS.ttsCharacter);

  const iconBg = `${theme.palette.primary.main}0D`;
  const primaryColor = theme.palette.primary.main;
  const border = `1px solid ${theme.palette.extendedGrey?.lighter}`;

  const hoursDisplay =
    !usageLoading && callRow ? formatUsedLimitPairAsHours(callRow.used, callRow.limit) : null;
  const llmUsage =
    !usageLoading && llmRow ? formatCompactUsagePair(llmRow.used, llmRow.limit) : null;
  const sttUsage =
    !usageLoading && sttRow ? formatCompactUsagePair(sttRow.used, sttRow.limit) : null;
  const ttsUsage =
    !usageLoading && ttsRow ? formatCompactUsagePair(ttsRow.used, ttsRow.limit) : null;
  const concurrentDisplay =
    !usageLoading && usageData?.concurrent != null
      ? `${usageData.concurrent.activeCalls} / ${usageData.concurrent.limit}`
      : null;
  const totalCampaignsDisplay =
    !overviewLoading && companyOverview?.totalCampaigns != null
      ? String(companyOverview.totalCampaigns)
      : null;
  const runningCampaignsDisplay =
    !overviewLoading && companyOverview?.runningCampaigns != null
      ? String(companyOverview.runningCampaigns)
      : null;
  const inactiveCampaignsDisplay =
    !overviewLoading && companyOverview?.inactiveCampaigns != null
      ? String(companyOverview.inactiveCampaigns)
      : null;

  return (
    <Box sx={{ ...gridContainerSx, marginTop: 3 }}>
      <MetricValueTooltip title={hoursDisplay ?? ''}>
        <BoxStyled onClick={() => {}} sx={{ border }}>
          <Stack direction="row" justifyContent="flex-start" spacing={2} alignItems="center">
            <IconBox sx={{ backgroundColor: iconBg }}>
              <IconClockHour4 size={22} stroke={1.5} color={primaryColor} />
            </IconBox>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h3" fontSize="17px" textAlign="start" noWrap>
                {usageLoading ? (
                  <CircularProgress size={14} />
                ) : callRow ? (
                  formatUsedLimitPairAsHours(callRow.used, callRow.limit)
                ) : (
                  '—'
                )}
              </Typography>
              <Typography variant="subtitle1" fontSize="12px" color={theme.palette.text.secondary}>
                {t('adminMainPage.cardFilter.hours')}
              </Typography>
            </Box>
          </Stack>
        </BoxStyled>
      </MetricValueTooltip>
      <MetricValueTooltip title={llmUsage?.full ?? ''}>
        <BoxStyled onClick={() => {}} sx={{ border }}>
          <Stack direction="row" justifyContent="flex-start" spacing={2} alignItems="center">
            <IconBox sx={{ backgroundColor: iconBg }}>
              <IconBrain size={22} stroke={1.5} color={primaryColor} />
            </IconBox>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h3" fontSize="17px" textAlign="start" noWrap>
                {usageLoading ? <CircularProgress size={14} /> : llmUsage ? llmUsage.short : '—'}
              </Typography>
              <Typography variant="subtitle1" fontSize="12px" color={theme.palette.text.secondary}>
                {t('adminMainPage.cardFilter.llmTokens')}
              </Typography>
            </Box>
          </Stack>
        </BoxStyled>
      </MetricValueTooltip>
      <MetricValueTooltip title={sttUsage?.full ?? ''}>
        <BoxStyled onClick={() => {}} sx={{ border }}>
          <Stack direction="row" justifyContent="flex-start" spacing={2} alignItems="center">
            <IconBox sx={{ backgroundColor: iconBg }}>
              <IconMicrophone size={22} stroke={1.5} color={primaryColor} />
            </IconBox>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h3" fontSize="17px" textAlign="start" noWrap>
                {usageLoading ? <CircularProgress size={14} /> : sttUsage ? sttUsage.short : '—'}
              </Typography>
              <Typography variant="subtitle1" fontSize="12px" color={theme.palette.text.secondary}>
                {t('adminMainPage.cardFilter.sttSeconds')}
              </Typography>
            </Box>
          </Stack>
        </BoxStyled>
      </MetricValueTooltip>
      <MetricValueTooltip title={ttsUsage?.full ?? ''}>
        <BoxStyled onClick={() => {}} sx={{ border }}>
          <Stack direction="row" justifyContent="flex-start" spacing={2} alignItems="center">
            <IconBox sx={{ backgroundColor: iconBg }}>
              <IconTypography size={22} stroke={1.5} color={primaryColor} />
            </IconBox>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h3" fontSize="17px" textAlign="start" noWrap>
                {usageLoading ? <CircularProgress size={14} /> : ttsUsage ? ttsUsage.short : '—'}
              </Typography>
              <Typography variant="subtitle1" fontSize="12px" color={theme.palette.text.secondary}>
                {t('adminMainPage.cardFilter.ttsCharacters')}
              </Typography>
            </Box>
          </Stack>
        </BoxStyled>
      </MetricValueTooltip>
      <MetricValueTooltip title={concurrentDisplay ?? ''}>
        <BoxStyled onClick={() => {}} sx={{ border }}>
          <Stack direction="row" justifyContent="flex-start" spacing={2} alignItems="center">
            <IconBox sx={{ backgroundColor: iconBg }}>
              <IconPhoneCall />
            </IconBox>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h3" fontSize="17px" textAlign="start" noWrap>
                {usageLoading ? (
                  <CircularProgress size={14} />
                ) : usageData?.concurrent != null ? (
                  `${usageData.concurrent.activeCalls} / ${usageData.concurrent.limit}`
                ) : (
                  '—'
                )}
              </Typography>
              <Typography variant="subtitle1" fontSize="12px" color={theme.palette.text.secondary}>
                {t('adminMainPage.cardFilter.concurrentCalls')}
              </Typography>
            </Box>
          </Stack>
        </BoxStyled>
      </MetricValueTooltip>
      <MetricValueTooltip title={totalCampaignsDisplay ?? ''}>
        <BoxStyled onClick={() => {}} sx={{ border }}>
          <Stack direction="row" justifyContent="flex-start" spacing={2} alignItems="center">
            <IconBox sx={{ backgroundColor: iconBg }}>
              <IconFileStack />
            </IconBox>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h3" fontSize="17px" textAlign="start" noWrap>
                {overviewLoading ? (
                  <CircularProgress size={14} />
                ) : (
                  (companyOverview?.totalCampaigns ?? '—')
                )}
              </Typography>
              <Typography variant="subtitle1" fontSize="12px" color={theme.palette.text.secondary}>
                {t('adminMainPage.cardFilter.totalCampaign')}
              </Typography>
            </Box>
          </Stack>
        </BoxStyled>
      </MetricValueTooltip>
      <MetricValueTooltip title={runningCampaignsDisplay ?? ''}>
        <BoxStyled onClick={() => {}} sx={{ border }}>
          <Stack direction="row" justifyContent="flex-start" spacing={2} alignItems="center">
            <IconBox sx={{ backgroundColor: iconBg }}>
              <IconFilePlay />
            </IconBox>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h3" fontSize="17px" textAlign="start" noWrap>
                {overviewLoading ? (
                  <CircularProgress size={14} />
                ) : (
                  (companyOverview?.runningCampaigns ?? '—')
                )}
              </Typography>
              <Typography variant="subtitle1" fontSize="12px" color={theme.palette.text.secondary}>
                {t('adminMainPage.cardFilter.runningCampaign')}
              </Typography>
            </Box>
          </Stack>
        </BoxStyled>
      </MetricValueTooltip>
      <MetricValueTooltip title={inactiveCampaignsDisplay ?? ''}>
        <BoxStyled onClick={() => {}} sx={{ border }}>
          <Stack direction="row" justifyContent="flex-start" spacing={2} alignItems="center">
            <IconBox sx={{ backgroundColor: iconBg }}>
              <IconFileStop />
            </IconBox>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h3" fontSize="17px" textAlign="start" noWrap>
                {overviewLoading ? (
                  <CircularProgress size={14} />
                ) : (
                  (companyOverview?.inactiveCampaigns ?? '—')
                )}
              </Typography>
              <Typography variant="subtitle1" fontSize="12px" color={theme.palette.text.secondary}>
                {t('adminMainPage.cardFilter.inactiveCampaign')}
              </Typography>
            </Box>
          </Stack>
        </BoxStyled>
      </MetricValueTooltip>
    </Box>
  );
};
